import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { Popconfirm } from "antd";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import CustomModal from "@/components/CustomModal";
import AliasAddForm from "@/components/BookingsDetails/PersonalInformationDetails/AliasAddForm";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "@/components/InmateDetails/styles";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const DETENTION_ALIASES_HEADERS = [
  { key: "lName", text: "LName" },
  { key: "fName", text: "FName" },
  { key: "mName", text: "MName" },
  { key: "dob", text: "Date of Birth" },
  { key: "ssn", text: "SSN" },
  { key: "suffix", text: "Suffix" },
  { key: "questionedIdentity", text: "Questioned Identity" },
];

const toBoolean = (value) => {
  if (typeof value === "string") {
    return ["true", "1", "y", "yes"].includes(value.trim().toLowerCase());
  }
  return !!value;
};

const normalizeAlias = (alias = {}) => {
  const id = alias?.id || alias?.tempId || uuidv4();
  return {
    ...alias,
    id,
    questionedIdentity: toBoolean(alias?.questionedIdentity),
  };
};

const stripLocalFields = (alias = {}) => {
  const { id, tempId, ...rest } = alias;
  return {
    ...rest,
    questionedIdentity: toBoolean(rest?.questionedIdentity),
  };
};

const DetentionAliasesList = ({
  aliases = [],
  disabled = false,
  onChange = () => {},
  startingPoint=""
}) => {
  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). This nested grid has no
  // bookingsFieldsEnabled of its own — `disabled` (passed down from the
  // Detention Details modal) is the equivalent "record is in edit mode"
  // signal here, same as ChargesInformation.jsx (DetentionsCharges).
  const {
    headers: detentionAliasesHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:DetentionsBioAliases", DETENTION_ALIASES_HEADERS);

  const aliasFormRef = useRef();
  const selectedAliasIndexRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAlias, setSelectedAlias] = useState(null);
  const [selectedAliasIndex, setSelectedAliasIndex] = useState(null);
  const [aliasData, setAliasData] = useState(() => aliases.map(normalizeAlias));

  useEffect(() => {
    const normalized = aliases.map(normalizeAlias);
    setAliasData(normalized);
  }, [aliases]);

  const emitChange = (updated) => {
    const sanitized = updated.map(stripLocalFields);
    onChange(sanitized);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setSelectedAlias(null);
    setSelectedAliasIndex(null);
    selectedAliasIndexRef.current = null;
    aliasFormRef.current?.resetForm();
  };

  // Open modal for adding a new alias
  const handleAdd = () => {
    if (disabled) return;
    setSelectedAlias(null);
    setSelectedAliasIndex(null);
    selectedAliasIndexRef.current = null;
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (index) => {
    let currentIndex = index;

    // If index is provided, use it
    if (currentIndex !== undefined && currentIndex !== null) {
      const currentAlias = aliasData[currentIndex] || null;

      setSelectedAlias(currentAlias);
      setSelectedAliasIndex(currentAlias ? currentIndex : null);
      selectedAliasIndexRef.current = currentAlias ? currentIndex : null;
    } else {
      // fallback to already selected index
      currentIndex = selectedAliasIndexRef.current;
    }

    if (currentIndex === null || currentIndex === undefined) return;

    setIsEditing(true);
    setShowModal(true);
    setSelectedAlias(aliasData[currentIndex]);
  };

  const handleDelete = () => {
    if (disabled || selectedAliasIndex === null) return;
    const filtered = aliasData.filter((_, index) => index !== selectedAliasIndex);
    setAliasData(filtered);
    emitChange(filtered);
    setSelectedAlias(null);
    setSelectedAliasIndex(null);
    selectedAliasIndexRef.current = null;
  };

  const handleRowClick = (_alias, index) => {
    if (typeof index === "undefined" || index === null) return;
    const currentAlias = aliasData[index] || null;
    setSelectedAlias(currentAlias);
    setSelectedAliasIndex(currentAlias ? index : null);
    selectedAliasIndexRef.current = currentAlias ? index : null;
  };

  const handleSave = () => {
    aliasFormRef.current
      ?.validateFields()
      .then((values) => {
        const payload = {
          ...values,
          questionedIdentity: toBoolean(values?.questionedIdentity),
        };
        if (payload.dob) {
          const dob = dayjs(payload.dob);
          payload.dob = dob.isValid() ? dob.format("MM/DD/YYYY") : null;
        }

        if (
          isEditing &&
          selectedAliasIndex !== null &&
          aliasData[selectedAliasIndex]
        ) {
          const updated = aliasData.map((alias, index) =>
            index === selectedAliasIndex
              ? normalizeAlias({ ...alias, ...payload })
              : alias
          );
          setAliasData(updated);
          emitChange(updated);
        } else {
          // Adding new alias
          const newAlias = normalizeAlias({
            id: uuidv4(),
            ...payload,
          });
          const updated = [...aliasData, newAlias];
          setAliasData(updated);
          emitChange(updated);
        }
        closeModal();
      })
      .catch(() => {});
  };

  const tableData = useMemo(
    () =>
      aliasData.map((alias) => ({
        ...alias,
        questionedIdentity: toBoolean(alias.questionedIdentity)
          ? "True"
          : "False",
      })),
    [aliasData]
  );

  return (
    <>
      <InmateDetailsTableContainer>
        <TableHeader>
          <h6>Detention Aliases</h6>
          <div className="right">
            <Button onClick={handleAdd} disabled={disabled || isConfiguring}>
              Add Alias
            </Button>
            <Button
              onClick={handleEdit}
              style={{ marginLeft: 10 }}
              disabled={selectedAliasIndex === null || isConfiguring}
            >
              Details
            </Button>
            <Popconfirm
              title="Delete Alias"
              description="Are you sure you want to delete this alias?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              disabled={disabled || selectedAliasIndex === null || isConfiguring}
            >
              <Button
                style={{ marginLeft: 10 }}
                disabled={disabled || selectedAliasIndex === null || isConfiguring}
              >
                Delete
              </Button>
            </Popconfirm>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                style={{ marginLeft: 10 }}
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ disabled: false }}
                cancelButtonProps={{ disabled: false }}
              >
                <Button style={{ marginLeft: 10 }} htmlType="button">
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </div>
        </TableHeader>

        <Table
          ref={dataTableRef}
          headers={detentionAliasesHeaders}
          draggable={isConfiguring}
          data={tableData}
          onRowClick={handleRowClick}
          rowClassName={(record) =>
            selectedAlias && record.id === selectedAlias.id ? "row-selected" : ""
          }
          onRowDoubleClick={(data, index) => {
            handleEdit(index);
          }}
        />
      </InmateDetailsTableContainer>

      <CustomModal
        header="Alias Information"
        show={showModal}
        onHide={closeModal}
        onSaveClick={handleSave}
        onCloseClick={closeModal}
        hideFooter={disabled}
        designPattern="ant"
        width="55%"
        bodyStyle={{ padding: 20 }}
        submitButtonDisabled={disabled}
      >
        {showModal && (
          <AliasAddForm
            ref={aliasFormRef}
            formData={selectedAlias}
            bookingsFieldsEnabled={!disabled}
            startingPoint={startingPoint}
            tableName={"DetentionsBioAliases"}
          />
        )}
      </CustomModal>
    </>
  );
};

export default DetentionAliasesList;
